/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * Help and documentation: the shape of the guide, and how it is searched.
 *
 * The guide is data rather than markup, so it is versioned with the release it
 * describes and searched without a server. Text carries one piece of inline
 * formatting only: an on-screen label wrapped in `**double asterisks**`, shown
 * as the label. Nothing in it is ever read as HTML.
 */

/** One piece of a topic. */
export type HelpBlock =
  | { kind: 'text'; heading?: string; text: string }
  | { kind: 'tip'; heading?: string; text: string }
  | { kind: 'warning'; heading?: string; text: string }
  | { kind: 'steps'; heading?: string; items: string[] }
  | { kind: 'bullets'; heading?: string; items: string[] }
  | { kind: 'table'; heading?: string; columns: string[]; rows: string[][] }
  | { kind: 'faq'; question: string; text: string };

/**
 * One topic somebody might look up.
 *
 * It answers at a glance first and in full on request: the `summary` bullets
 * are always on screen, and the `blocks` are the detail folded beneath them.
 */
export interface HelpSection {
  id: string;
  title: string;
  /** One line saying what the topic is about, under its title. */
  lede: string;
  /**
   * What it is, who can do it if not everyone, and the steps with the labels
   * as on screen: one to five bullets, at most 80 words in all (`HELP-1`).
   */
  summary: string[];
  /** Who can do this, by role. Absent when anybody signed in can. */
  who?: string;
  /** Words somebody might search for that the text does not use. */
  keywords?: string[];
  /** The detail, folded under More detail until opened or searched. */
  blocks: HelpBlock[];
}

/** A part of the guide: a group of topics, as the contents list shows them. */
export interface HelpPart {
  id: string;
  title: string;
  sections: HelpSection[];
}

/** A run of text, and whether it is an on-screen label. */
export interface Inline {
  text: string;
  label: boolean;
}

/**
 * Split text into plain runs and `**labels**`.
 *
 * An unclosed `**` is left as it was typed rather than swallowing the rest of
 * the sentence.
 */
export function inline(text: string): Inline[] {
  const runs: Inline[] = [];
  const pattern = /\*\*(.+?)\*\*/g;
  let at = 0;
  for (const match of text.matchAll(pattern)) {
    const start = match.index;
    if (start > at) runs.push({ text: text.slice(at, start), label: false });
    runs.push({ text: match[1] ?? '', label: true });
    at = start + match[0].length;
  }
  if (at < text.length) runs.push({ text: text.slice(at), label: false });
  return runs;
}

/**
 * What each topic says, worked out once. The guide never changes while the
 * page is open, so a keystroke searches strings already built rather than
 * rebuilding every one of them.
 */
const said = new WeakMap<HelpSection, string>();

/** Everything a topic says, as one lower-case string to search. */
function haystack(section: HelpSection): string {
  const known = said.get(section);
  if (known !== undefined) return known;
  const text = gather(section);
  said.set(section, text);
  return text;
}

function gather(section: HelpSection): string {
  const parts: string[] = [
    section.title,
    section.lede,
    ...section.summary,
    section.who ?? '',
  ];
  parts.push(...(section.keywords ?? []));
  for (const block of section.blocks) {
    if ('heading' in block && block.heading) parts.push(block.heading);
    switch (block.kind) {
      case 'text':
      case 'tip':
      case 'warning':
        parts.push(block.text);
        break;
      case 'faq':
        parts.push(block.question, block.text);
        break;
      case 'steps':
      case 'bullets':
        parts.push(...block.items);
        break;
      case 'table':
        parts.push(...block.columns, ...block.rows.flat());
        break;
    }
  }
  return parts.join(' ').replaceAll('**', '').toLowerCase();
}

/** The words of a search, lower-cased, with nothing empty. */
export function terms(query: string): string[] {
  return query
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.trim())
    .filter((word) => word.length > 0);
}

/**
 * The parts, with only the topics that mention every word searched for.
 *
 * Every word, not any: "change signer" should find the topic about changing a
 * signer, not every topic that says "change". A part with nothing left is
 * dropped, so the contents list never shows an empty heading.
 */
export function searchHelp(parts: HelpPart[], query: string): HelpPart[] {
  const words = terms(query);
  if (words.length === 0) return parts;
  return parts
    .map((part) => ({
      ...part,
      sections: part.sections.filter((section) => {
        const text = haystack(section);
        return words.every((word) => text.includes(word));
      }),
    }))
    .filter((part) => part.sections.length > 0);
}

/** A topic found from the search in the top bar, with the part it is in. */
export interface HelpMatch {
  part: string;
  section: HelpSection;
}

/**
 * The few topics that best answer a search made from anywhere in Vellum.
 *
 * The same every-word rule as the help page, then those whose title mentions
 * every word ahead of those that only mention it somewhere inside: somebody
 * typing "change signer" wants the topic called that before the glossary that
 * uses both words. At most `limit`, because these sit under the agreements and
 * people that search is mostly for.
 */
export function helpMatches(parts: HelpPart[], query: string, limit = 5): HelpMatch[] {
  const words = terms(query);
  if (words.length === 0) return [];
  const found = searchHelp(parts, query).flatMap((part) =>
    part.sections.map((section) => ({ part: part.title, section })),
  );
  const inTitle = (match: HelpMatch) => {
    const title = match.section.title.toLowerCase();
    return words.every((word) => title.includes(word));
  };
  return [...found.filter(inTitle), ...found.filter((match) => !inTitle(match))].slice(
    0,
    limit,
  );
}

/** How many topics the parts hold. */
export function topicCount(parts: HelpPart[]): number {
  return parts.reduce((sum, part) => sum + part.sections.length, 0);
}

/** How many words a piece of text has, its `**` marks aside. */
export function wordsIn(text: string): number {
  return text
    .replaceAll('**', '')
    .split(/\s+/)
    .filter((word) => word.length > 0).length;
}

/** Every string a topic shows on screen, summary first. Keywords are not shown. */
export function shownText(section: HelpSection): string[] {
  const out = [section.title, section.lede, ...section.summary];
  if (section.who) out.push(section.who);
  for (const block of section.blocks) {
    if ('heading' in block && block.heading) out.push(block.heading);
    switch (block.kind) {
      case 'text':
      case 'tip':
      case 'warning':
        out.push(block.text);
        break;
      case 'faq':
        out.push(block.question, block.text);
        break;
      case 'steps':
      case 'bullets':
        out.push(...block.items);
        break;
      case 'table':
        out.push(...block.columns, ...block.rows.flat());
        break;
    }
  }
  return out;
}

/** How long the guide is: every word shown, and the words of the summaries. */
export function guideLength(parts: HelpPart[]): { words: number; summaryWords: number } {
  let words = 0;
  let summaryWords = 0;
  for (const part of parts) {
    words += wordsIn(part.title);
    for (const section of part.sections) {
      for (const text of shownText(section)) words += wordsIn(text);
      for (const bullet of section.summary) summaryWords += wordsIn(bullet);
    }
  }
  return { words, summaryWords };
}

/**
 * Where a link into the guide should land.
 *
 * Topics merged into others keep their old addresses working (`HELP-6`): a
 * `/help#<old-id>` sent in an email months ago opens the topic that absorbed
 * it. An id the guide still has, or one it never had, is returned as it is.
 */
export function landingId(id: string, retired: Readonly<Record<string, string>>): string {
  return Object.hasOwn(retired, id) ? (retired[id] ?? id) : id;
}
